import {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";

const STORAGE_PREFIX = "empoweredman.draft.v1";

function sanitizeSegment(segment) {
  if (segment === null || segment === undefined) return "";
  return String(segment).replace(/[^\w.-]/g, "_");
}

/**
 * Builds a stable localStorage key: per user, scope (e.g. chat id), and field name.
 */
export function getLocalDraftKey(userId, scopeId, fieldKey) {
  if (
    userId == null ||
    userId === "" ||
    scopeId == null ||
    scopeId === "" ||
    !fieldKey
  ) {
    return null;
  }
  return `${STORAGE_PREFIX}:${sanitizeSegment(userId)}:${sanitizeSegment(scopeId)}:${sanitizeSegment(fieldKey)}`;
}

/**
 * Persists a field value per logged-in user and scope (e.g. conversation) in localStorage.
 * Use for plain text inputs, textareas, search boxes, etc.
 *
 * @param {object} options
 * @param {string|number|null} options.userId - Current user id (required for persistence)
 * @param {string|number|null} options.scopeId - Chat id, page id, or other grouping key
 * @param {string} options.fieldKey - Unique name for this input within the scope (e.g. "message", "search")
 * @param {number} [options.debounceMs=400]
 * @param {string} [options.initialValue=""]
 * @param {boolean} [options.enabled=true] - Set false to disable read/write (e.g. while editing from server data)
 */
export function useLocalDraft({
  userId,
  scopeId,
  fieldKey,
  debounceMs = 400,
  initialValue = "",
  enabled = true,
}) {
  const serializeRef = useRef((v) =>
    v === null || v === undefined ? "" : String(v),
  );
  const deserializeRef = useRef((s) =>
    s === "" || s == null ? "" : String(s),
  );

  const storageKey = useMemo(() => {
    if (!enabled) return null;
    return getLocalDraftKey(userId, scopeId, fieldKey);
  }, [userId, scopeId, fieldKey, enabled]);

  const [value, setValueState] = useState(initialValue);
  const debounceTimerRef = useRef(null);

  useLayoutEffect(() => {
    if (!storageKey) {
      setValueState(initialValue);
      return;
    }
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw === null) {
        setValueState(initialValue);
      } else {
        setValueState(deserializeRef.current(raw));
      }
    } catch {
      setValueState(initialValue);
    }
  }, [storageKey, initialValue]);

  const persistNow = useCallback(
    (next) => {
      if (!storageKey) return;
      try {
        const serialized = serializeRef.current(next);
        if (serialized === "" || serialized == null) {
          localStorage.removeItem(storageKey);
        } else {
          localStorage.setItem(storageKey, serialized);
        }
      } catch (e) {
        console.warn("useLocalDraft: persist failed", e);
      }
    },
    [storageKey],
  );

  const flush = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, []);

  const setValue = useCallback(
    (next) => {
      setValueState((prev) => {
        const resolved = typeof next === "function" ? next(prev) : next;
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
        debounceTimerRef.current = setTimeout(() => {
          persistNow(resolved);
          debounceTimerRef.current = null;
        }, debounceMs);
        return resolved;
      });
    },
    [debounceMs, persistNow],
  );

  const clearDraft = useCallback(() => {
    flush();
    if (storageKey) {
      try {
        localStorage.removeItem(storageKey);
      } catch {
        /* ignore */
      }
    }
    setValueState(initialValue);
  }, [storageKey, initialValue, flush]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return {
    value,
    setValue,
    clearDraft,
    storageKey,
    /** Immediately write current value to localStorage (e.g. before navigation) */
    flushToStorage: useCallback(() => {
      flush();
      persistNow(value);
    }, [flush, persistNow, value]),
  };
}

/**
 * JSON in localStorage. Same keying as useLocalDraft.
 *
 * When `syncValueToState` is false (default), `setValue` only writes to localStorage (debounced)
 * and does **not** update React `value`. `value` only updates when `storageKey` changes (e.g. new chat).
 * Use this for Draft.js or other heavy editors where the parent owns the live state.
 *
 * `syncValueToState: true` matches useLocalDraft behaviour (each keystroke updates `value`).
 */
export function useLocalDraftJson({
  userId,
  scopeId,
  fieldKey,
  debounceMs = 400,
  initialValue = null,
  enabled = true,
  syncValueToState = false,
}) {
  const storageKey = useMemo(() => {
    if (!enabled) return null;
    return getLocalDraftKey(userId, scopeId, fieldKey);
  }, [userId, scopeId, fieldKey, enabled]);

  const [value, setValueState] = useState(initialValue);
  const debounceTimerRef = useRef(null);

  useLayoutEffect(() => {
    if (!storageKey) {
      setValueState(initialValue);
      return;
    }
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw === null || raw === "") {
        setValueState(initialValue);
      } else {
        setValueState(JSON.parse(raw));
      }
    } catch {
      setValueState(initialValue);
    }
  }, [storageKey, initialValue]);

  const persistNow = useCallback(
    (next) => {
      if (!storageKey) return;
      try {
        if (next === null || next === undefined) {
          localStorage.removeItem(storageKey);
          return;
        }
        localStorage.setItem(storageKey, JSON.stringify(next));
      } catch (e) {
        console.warn("useLocalDraftJson: persist failed", e);
      }
    },
    [storageKey],
  );

  const flush = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, []);

  const setValue = useCallback(
    (next) => {
      setValueState((prev) => {
        const resolved = typeof next === "function" ? next(prev) : next;
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
        debounceTimerRef.current = setTimeout(() => {
          persistNow(resolved);
          debounceTimerRef.current = null;
        }, debounceMs);
        return syncValueToState ? resolved : prev;
      });
    },
    [debounceMs, persistNow, syncValueToState],
  );

  const clearDraft = useCallback(() => {
    flush();
    if (storageKey) {
      try {
        localStorage.removeItem(storageKey);
      } catch {
        /* ignore */
      }
    }
    setValueState(initialValue);
  }, [storageKey, initialValue, flush]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return {
    value,
    setValue,
    clearDraft,
    storageKey,
    flushToStorage: useCallback(
      (payload) => {
        flush();
        const next = payload !== undefined ? payload : value;
        persistNow(next);
      },
      [flush, persistNow, value],
    ),
  };
}
